import type { ExpandedState } from '@tanstack/react-table';
import type { HeaderConfig, HeaderDisplayValue, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { Spinner } from '@tempo-io/sui-spinner';
import { addMonths, format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomField } from '@/lib/api/types/customField';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  IPropertySelectorByID,
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
  IVisualizationHeader,
  IVisualizationHeaderProps,
} from '@/lib/api/types/loop-visualization';
import type { Team } from '@/lib/api/types/teams';
import { useLocalStorage } from '@/lib/hooks/common/useLocalStorage';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import { useUpdateVisualizationItemMutation } from '@/lib/mutations/loop-visualization/items/useUpdateVisualizationItemMutation';
import { useUpsertTimelineLayoutMutation } from '@/lib/mutations/loop-visualization/useTimelineLayoutMutations';
import { useGetCustomFieldsQuery } from '@/lib/queries/common/useGetCustomFieldsQuery';
import {
  useTimelineHeaderConfig,
  useTimelineSubHeaderConfigs,
} from '@/lib/queries/loop-visualization/useTimelineHeaderConfig';
import { useVisualizationItemRanks } from '@/lib/queries/loop-visualization/useVisualizationItemRanks';
import { useViewDateSource } from '@/lib/queries/view/useViewDateSource';
import { useGetSchedulesByItemIds } from '@/lib/queries/work-integrations/useGetSchedules';
import { toISOTimestamp } from '@/lib/utils/common/dateFormats';
import { DEFAULT_FALLBACK_COLOR } from '@/lib/utils/loop-visualization/colors';
import { headerToPropertySelector } from '@/lib/utils/loop-visualization/commonLayoutUtils';
import { getItemFieldValue } from '@/lib/utils/loop-visualization/fieldExtraction';
import { isCustomFieldApplicableToItemType } from '@/lib/utils/loop-visualization/isCustomFieldApplicableToItemType';
import {
  getEditableDateFields,
  isForecastDateSource,
  remapItemDates,
} from '@/lib/utils/loop-visualization/itemDateSource';
import {
  applyHeaderValueToUpdate,
  createEmptyUpdateInput,
} from '@/lib/utils/loop-visualization/visualizationItemUpdate';
import { VisualizationNoHeadersState } from '../empty-state/VisualizationNoHeadersState';
import { VisualizationNoItemsMatchFiltersState } from '../empty-state/VisualizationNoItemsMatchFiltersState';
import { VisualizationNoItemsMatchLayoutState } from '../empty-state/VisualizationNoItemsMatchLayoutState';
import { VisualizationNoItemsState } from '../empty-state/VisualizationNoItemsState';
import { Legend } from '../published/Legend';
import { COMPOSITE_ID_SEPARATOR, NO_VALUE_ID } from '../utils/constants';
import { generateHeaders } from '../utils/generateHeaders';
import { buildItemLabelsMap } from '../utils/labelByUtils';
import { useRowHeaderWidth } from '../utils/useRowHeaderWidth';
import { useVisualizationHeaderOptions } from '../utils/useVisualizationHeaderOptions';
import {
  extractCustomFieldIdsFromLayout,
  getHeaderFieldId,
  getHeaderItemKey,
} from '../utils/visualizationHeaderUtils';
import { useViewContext } from '../ViewContext';
import { TimelineMinimap } from './minimap/TimelineMinimap';
import { ReorderTimelineHeadersDialog } from './ReorderTimelineHeadersDialog';
import { TimelineAddHeaderMenu } from './TimelineAddHeaderMenu';
import { TimelineNoItemsWithDates } from './TimelineNoItemsWithDates';
import { TimelineRubberBand } from './TimelineRubberBand';
import { TimelineSelectionActionBar } from './TimelineSelectionActionBar';
import { TimelineTable } from './TimelineTable';
import { useTimelineItemsWithChildren } from './useFilteredTimelineItems';
import { useTimelineColorContext } from './useTimelineColorContext';
import { useTimelineMainHeaders, useTimelineSubHeaders } from './useTimelineHeaderColors';
import { useTimelineItemLevels } from './useTimelineItemLevels';
import { useTimelineLazyChildren } from './useTimelineLazyChildren';
import { useTimelineMinimapDates } from './useTimelineMinimapDates';
import { useTimelineSelection } from './useTimelineSelection';
import type { TimelineRowType } from './useTimelineTableColumns';
import { useTimelineTableData } from './useTimelineTableData';

const EMPTY_CHILD_IDS_BY_PARENT: ReadonlyMap<number, number[]> = new Map();
const EMPTY_EXPANDED_CHILDREN: Record<string, boolean> = {};

export interface TimelineProps {
  items: ItemV2[];
  allItemsById?: ReadonlyMap<number, Partial<ItemV2>>;
  childIdsByParentId?: ReadonlyMap<number, number[]>;
  itemTypes: ItemTypeOption[];
  itemLevels: ItemTypeLevelOption[];
  itemStatuses: ItemStatusOption[];
  layout: TimelineLayoutDTO;
  visualizationId: number;
  hasFilters: boolean;
  hasItemsIgnoringLayout?: boolean;
  from: string | null;
  to: string | null;
  onDateChange: (dates: { from: string; to: string }) => void;
  canEdit: boolean;
  viewLocked?: boolean;
  isPublishedMode?: boolean;
  isExportMode?: boolean;
  publishedLegendItems?: { color: string; label: string; isHidden?: boolean }[];
  publishedItemRanks?: Map<number, string>;
  mainHeaderConfig?: HeaderConfig;
  subHeaderConfigs?: Record<string, HeaderConfig>;
  /**
   * Server-resolved per-header display values (e.g. MULTI_CUSTOMER chips) for
   * published mode. The client cannot hit FIM, so the backend pre-populates
   * this map keyed by stringified header id.
   */
  publishedHeaderDisplayValuesByHeaderId?: Record<string, HeaderDisplayValue[]>;
  teamsOverride?: Team[];
  onItemClick?: (itemId: number) => void;
  hideLegendOverlay?: boolean;
  isFetching?: boolean;
  /** Render the minimap even in published mode (e.g. dashboard panels). */
  showMinimap?: boolean;
  onOpenFormatPanel?: () => void;
}

/**
 * Merge published-mode header display values into the raw header list.
 *
 * When `rawMainHeaders` is empty but the server has pre-computed
 * `orderedHeaderIds` (e.g. MULTI_CUSTOMER swimlane headers), synthesize
 * `IVisualizationHeaderProps` from the ordered ids. Synthesis is skipped
 * when `timelineHeader` cannot yield a valid row selector (e.g. DateSelector
 * or null), since we would otherwise produce headers with an incorrect
 * `customPropertyID`.
 */
export function mergePublishedMainHeaders(
  rawMainHeaders: IVisualizationHeaderProps[],
  dvMap: Record<string, HeaderDisplayValue[]>,
  orderedHeaderIds: (string | number)[],
  timelineHeader: IPropertySelectorByID | null,
  includeCustomerDisplayValues = true,
): IVisualizationHeaderProps[] {
  if (!includeCustomerDisplayValues) {
    return rawMainHeaders;
  }

  if (rawMainHeaders.length === 0 && orderedHeaderIds.length > 0) {
    const validSelector =
      timelineHeader?.type === 'CustomPropertySelectorByID' ||
      timelineHeader?.type === 'ParentItemSelector' ||
      timelineHeader?.type === 'createdBy'
        ? timelineHeader
        : null;

    if (!validSelector) return rawMainHeaders;

    return orderedHeaderIds.map((rawId) => {
      const id = String(rawId);
      const displayValues = dvMap[id];
      const customer = displayValues?.find(
        (dv): dv is Extract<HeaderDisplayValue, { type: 'customer' }> => dv.type === 'customer',
      );
      return {
        id,
        label: customer?.name ?? id,
        displayValues,
        headerColor: DEFAULT_FALLBACK_COLOR,
        isDateHeader: false,
        selector: validSelector,
      };
    });
  }

  return rawMainHeaders.map((header) => {
    const id = header.id?.toString() ?? '';
    const dv = dvMap[id];
    return dv ? { ...header, displayValues: dv } : header;
  });
}

export const Timeline = ({
  items,
  itemTypes,
  itemLevels,
  itemStatuses,
  layout,
  visualizationId,
  hasFilters,
  hasItemsIgnoringLayout = false,
  from,
  to,
  onDateChange,
  canEdit,
  viewLocked: _viewLocked = false,
  isPublishedMode = false,
  isExportMode = false,
  publishedLegendItems,
  mainHeaderConfig: mainHeaderConfigProp,
  subHeaderConfigs: subHeaderConfigsProp,
  publishedHeaderDisplayValuesByHeaderId,
  teamsOverride,
  publishedItemRanks,
  isFetching,
  onItemClick,
  hideLegendOverlay = false,
  allItemsById,
  childIdsByParentId,
  showMinimap = false,
  onOpenFormatPanel,
}: TimelineProps) => {
  const { t } = useTranslation();
  const { viewId } = useViewContext();
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');
  const [expandedRows, setExpandedRows] = useState<ExpandedState>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [expandedChildren, setExpandedChildren] = useLocalStorage<Record<string, boolean>>(
    `timeline-expanded-children-${visualizationId}`,
    EMPTY_EXPANDED_CHILDREN,
  );
  const [visibleItemIds, setVisibleItemIds] = useState<ReadonlySet<number>>(() => new Set());
  const [reorderHeadersModalState, setReorderHeadersModalState] = useState<{
    isReorderingMainHeader: boolean;
    parentHeaderId: string | null;
  } | null>(null);

  const { mutate: upsertLayout } = useUpsertTimelineLayoutMutation(viewId, visualizationId);
  const { mutate: updateVisualizationItem } = useUpdateVisualizationItemMutation();
  const { data: customFields } = useGetCustomFieldsQuery({
    enabled: canEdit && !isPublishedMode,
  });
  const customFieldsById = useMemo(
    () => new Map<number, CustomField>((customFields ?? []).map((f) => [f.id, f])),
    [customFields],
  );
  const { data: fetchedItemRanks } = useVisualizationItemRanks({
    viewId,
    visualizationId,
    enabled: !isPublishedMode || publishedItemRanks === undefined,
  });
  const itemRanks = publishedItemRanks ?? fetchedItemRanks;

  const { rowHeaderWidth, setRowHeaderWidth } = useRowHeaderWidth({
    layoutWidth: layout.rowHeaderWidth,
    defaultWidth: 120,
    mutate: upsertLayout,
  });

  const effectiveFrom = from ?? layout.startDate ?? null;
  const effectiveTo = to ?? layout.endDate ?? null;

  useEffect(() => {
    if (from !== null && to !== null) return;
    const today = new Date();
    onDateChange({
      from: layout.startDate ?? format(today, 'yyyy-MM-dd'),
      to: layout.endDate ?? format(addMonths(today, 6), 'yyyy-MM-dd'),
    });
  }, [from, to, onDateChange, layout.startDate, layout.endDate]);

  const { curLevelItemTypes, nextLevelItemTypes } = useTimelineItemLevels({
    itemLevelId: layout.itemLevelId,
    itemTypes,
    itemLevels,
  });

  const resolvedChildIdsByParentId = childIdsByParentId ?? EMPTY_CHILD_IDS_BY_PARENT;
  const lazyChildCustomFieldIds = useMemo(() => extractCustomFieldIdsFromLayout(layout), [layout]);

  const { childrenByParentId, loadingParentIds } = useTimelineLazyChildren({
    childIdsByParentId: resolvedChildIdsByParentId,
    expandedChildren,
    visibleParentIds: visibleItemIds,
    customFieldIds: lazyChildCustomFieldIds,
  });

  const { data: dateSource } = useViewDateSource(viewId, { enabled: !isPublishedMode });
  const isForecast = isForecastDateSource(dateSource);
  const editableDateFields = getEditableDateFields(dateSource);
  const canEditDates = canEdit && editableDateFields !== null;

  const forecastItemIds = useMemo(() => {
    if (!isForecast) return [];
    const ids = items.map((item) => item.id);
    for (const children of childrenByParentId.values()) {
      for (const child of children) if (child.id !== undefined) ids.push(child.id);
    }
    return ids;
  }, [isForecast, items, childrenByParentId]);

  const { data: scheduleById } = useGetSchedulesByItemIds(forecastItemIds, undefined, isForecast);

  const remappedItems = useMemo(
    () => remapItemDates(items, dateSource, scheduleById),
    [items, dateSource, scheduleById],
  );

  const remappedChildrenByParentId = useMemo(() => {
    if (!dateSource || dateSource.dateSourceType === 'PLAN') return childrenByParentId;
    const map = new Map<number, Partial<ItemV2>[]>();
    for (const [parentId, children] of childrenByParentId) {
      map.set(parentId, remapItemDates(children, dateSource, scheduleById));
    }
    return map;
  }, [childrenByParentId, dateSource, scheduleById]);

  const filteredItemsWithChildren = useTimelineItemsWithChildren({
    from: effectiveFrom,
    to: effectiveTo,
    items: remappedItems,
    targetLevelItemTypes: curLevelItemTypes,
    nextLevelItemTypes,
    showChildItems: layout.showChildItems ?? false,
    childIdsByParentId: resolvedChildIdsByParentId,
    childrenByParentId: remappedChildrenByParentId,
    loadingParentIds,
  });

  const itemsData = useMemo(
    () => ({
      items: filteredItemsWithChildren,
      itemTypes,
      itemLevels,
      itemStatuses,
    }),
    [filteredItemsWithChildren, itemTypes, itemLevels, itemStatuses],
  );

  const timelineHeader = useMemo(
    () => headerToPropertySelector(layout.header1 ?? null),
    [layout.header1],
  );
  const timelineSubHeader = useMemo(
    () => headerToPropertySelector(layout.header2 ?? null),
    [layout.header2],
  );

  const colorContextItems = useMemo(
    () => remappedItems.filter((item) => item.plannedStartDateTime || item.plannedFinishDateTime),
    [remappedItems],
  );

  const {
    currentColorByField: contextColorByField,
    orderedHeaderValues: contextOrderedHeaderValues,
    paletteSwatches,
    valueToColorIndex,
  } = useTimelineColorContext({
    items: colorContextItems,
    itemTypes,
    itemLevels,
    itemStatuses,
    noValueLabel: t(($) => $.loopVisualization.layout.formatPanel.colors.noValue),
    isPublishedMode,
    publishedLayout: isPublishedMode ? layout : undefined,
    publishedMainHeaderConfig: isPublishedMode ? mainHeaderConfigProp : undefined,
    allItemsById,
    teamsOverride,
  });

  const timelineHeaderOptions = useVisualizationHeaderOptions(
    timelineHeader,
    remappedItems,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
      allItemsById,
    },
  ) as IVisualizationHeader;

  const timelineSubHeaderOptions = useVisualizationHeaderOptions(
    timelineSubHeader,
    remappedItems,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
      allItemsById,
    },
  ) as IVisualizationHeader;

  const showEmptyHeaders = (layout.showEmptyHeaders ?? false) && !hasFilters;

  const header1FieldId = useMemo(
    () => getHeaderFieldId(timelineHeaderOptions),
    [timelineHeaderOptions],
  );
  const header2FieldId = useMemo(
    () => getHeaderFieldId(timelineSubHeaderOptions),
    [timelineSubHeaderOptions],
  );

  const includeNullParentItemHeader = useCallback(
    (headerOptions: IVisualizationHeader | null) => {
      if (headerOptions?.type !== 'ParentItemSelector') return false;
      if (showEmptyHeaders) return true;

      const parentIds = new Set(
        headerOptions.options
          .map((option) => option.id)
          .filter((id): id is number => typeof id === 'number'),
      );
      return items.some((item) => item.parentId == null || !parentIds.has(item.parentId));
    },
    [items, showEmptyHeaders],
  );

  const includeNullHeader1 = includeNullParentItemHeader(timelineHeaderOptions);
  const includeNullHeader2 = includeNullParentItemHeader(timelineSubHeaderOptions);

  const labelByHeader1Selector = useMemo(
    () => headerToPropertySelector(layout.labelByHeader1 ?? null),
    [layout.labelByHeader1],
  );
  const labelByHeader2Selector = useMemo(
    () => headerToPropertySelector(layout.labelByHeader2 ?? null),
    [layout.labelByHeader2],
  );

  const labelByHeader1Options = useVisualizationHeaderOptions(
    labelByHeader1Selector,
    remappedItems,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
      allItemsById,
    },
  ) as IVisualizationHeader | null;
  const labelByHeader2Options = useVisualizationHeaderOptions(
    labelByHeader2Selector,
    remappedItems,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
      allItemsById,
    },
  ) as IVisualizationHeader | null;

  const labelBy1Headers = useMemo(
    () =>
      labelByHeader1Options
        ? generateHeaders({
            header: labelByHeader1Options,
            items: remappedItems,
            includeCustomerDisplayValues: ideaManagerEnabled,
          })
        : [],
    [labelByHeader1Options, remappedItems, ideaManagerEnabled],
  );
  const labelBy2Headers = useMemo(
    () =>
      labelByHeader2Options
        ? generateHeaders({
            header: labelByHeader2Options,
            items: remappedItems,
            includeCustomerDisplayValues: ideaManagerEnabled,
          })
        : [],
    [labelByHeader2Options, remappedItems, ideaManagerEnabled],
  );

  const itemLabelsById = useMemo(
    () =>
      buildItemLabelsMap({
        items: filteredItemsWithChildren,
        labelByHeader1: labelByHeader1Options ?? null,
        labelByHeader2: labelByHeader2Options ?? null,
        labelBy1Headers,
        labelBy2Headers,
      }),
    [
      filteredItemsWithChildren,
      labelByHeader1Options,
      labelByHeader2Options,
      labelBy1Headers,
      labelBy2Headers,
    ],
  );

  const effectiveColorByField = contextColorByField || 'status';
  const orderedHeaderValues = contextOrderedHeaderValues;

  const rawMainHeaders = useTimelineMainHeaders({
    timelineHeaderOptions,
    colorConfig: layout.colorConfig,
    header1FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader: includeNullHeader1,
  });

  const subHeaders = useTimelineSubHeaders({
    timelineSubHeaderOptions,
    colorConfig: layout.colorConfig,
    header2FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader: includeNullHeader2,
  });

  const mainHeaders = useMemo(() => {
    if (!isPublishedMode) return rawMainHeaders;
    return mergePublishedMainHeaders(
      rawMainHeaders,
      publishedHeaderDisplayValuesByHeaderId ?? {},
      mainHeaderConfigProp?.orderedHeaderIds ?? [],
      timelineHeader,
      ideaManagerEnabled,
    );
  }, [
    isPublishedMode,
    rawMainHeaders,
    publishedHeaderDisplayValuesByHeaderId,
    mainHeaderConfigProp,
    timelineHeader,
    ideaManagerEnabled,
  ]);

  const itemColorById = useMemo(() => {
    const colorConfig = layout.colorConfig;
    if (!colorConfig || colorConfig.mode !== 'palette') return undefined;
    if (!effectiveColorByField) return undefined;

    const result: Record<number, string> = {};
    for (const item of remappedItems) {
      const fieldValue = getItemFieldValue(item, effectiveColorByField);
      const resolvedValue = fieldValue ?? NO_VALUE_ID;
      const index = valueToColorIndex[resolvedValue];
      result[item.id] =
        index === undefined
          ? DEFAULT_FALLBACK_COLOR
          : paletteSwatches.itemColors[index % paletteSwatches.itemColors.length] ||
            DEFAULT_FALLBACK_COLOR;
    }
    return result;
  }, [
    remappedItems,
    layout.colorConfig,
    effectiveColorByField,
    paletteSwatches.itemColors,
    valueToColorIndex,
  ]);

  const { data: mainHeaderConfigQuery } = useTimelineHeaderConfig({
    viewId,
    visualizationId,
    headerFieldId: isPublishedMode ? null : header1FieldId,
  });

  const subHeaderConfigsQuery = useTimelineSubHeaderConfigs({
    viewId,
    visualizationId,
    headerFieldId: header2FieldId,
    parentFieldId: header1FieldId,
    headers: isPublishedMode ? [] : mainHeaders,
  });

  const mainHeaderConfig = isPublishedMode ? mainHeaderConfigProp : mainHeaderConfigQuery;
  const subHeaderConfigs = isPublishedMode ? subHeaderConfigsProp : subHeaderConfigsQuery;

  const handleItemDrop = useCallback(
    (
      itemId: number,
      row: TimelineRowType,
      newStartDate: string,
      newEndDate: string,
      sourceRow?: TimelineRowType,
    ) => {
      if (!canEdit || !editableDateFields) {
        return;
      }
      const headerFieldId = getHeaderFieldId(timelineHeaderOptions);
      const itemHeaderPropertyKey = getHeaderItemKey(headerFieldId);
      const itemHeaderValue = row.parentHeaderId ? row.parentHeaderId : row.headerId;
      const sourceHeaderValue = sourceRow
        ? sourceRow.parentHeaderId
          ? sourceRow.parentHeaderId
          : sourceRow.headerId
        : undefined;

      const subHeaderFieldId = getHeaderFieldId(timelineSubHeaderOptions);
      const itemSubHeaderPropertyKey = getHeaderItemKey(subHeaderFieldId);
      const itemSubHeaderValue = row.parentHeaderId ? row.headerId : null;
      const sourceSubHeaderValue = sourceRow?.parentHeaderId ? sourceRow.headerId : null;

      const updateInput = createEmptyUpdateInput(itemId);
      const combineMultiSelect = layout.combineMultiSelectHeaders ?? false;
      const item = items.find((i) => i.id === itemId);

      if (itemHeaderPropertyKey && itemHeaderValue !== undefined) {
        const parsedHeaderFieldId = Number(headerFieldId);
        const headerField = !Number.isNaN(parsedHeaderFieldId)
          ? customFieldsById.get(parsedHeaderFieldId)
          : undefined;
        const headerRestricted =
          !!headerField &&
          !!item &&
          !isCustomFieldApplicableToItemType(headerField, item.itemTypeId);

        if (!headerRestricted) {
          applyHeaderValueToUpdate({
            updateInput,
            propertyKey: itemHeaderPropertyKey,
            targetValue: itemHeaderValue,
            sourceValue: sourceHeaderValue,
            customFields: item?.customFieldValues,
            noValueId: NO_VALUE_ID,
            compositeIdSeparator: COMPOSITE_ID_SEPARATOR,
            combineMultiSelect,
          });
        }

        if (itemSubHeaderPropertyKey && itemSubHeaderValue !== null) {
          const parsedSubHeaderFieldId = Number(subHeaderFieldId);
          const subHeaderField = !Number.isNaN(parsedSubHeaderFieldId)
            ? customFieldsById.get(parsedSubHeaderFieldId)
            : undefined;
          const subHeaderRestricted =
            !!subHeaderField &&
            !!item &&
            !isCustomFieldApplicableToItemType(subHeaderField, item.itemTypeId);

          if (!subHeaderRestricted) {
            applyHeaderValueToUpdate({
              updateInput,
              propertyKey: itemSubHeaderPropertyKey,
              targetValue: itemSubHeaderValue,
              sourceValue: sourceSubHeaderValue,
              customFields: item?.customFieldValues,
              noValueId: NO_VALUE_ID,
              compositeIdSeparator: COMPOSITE_ID_SEPARATOR,
              combineMultiSelect,
            });
          }
        }
      }

      updateInput.itemData[editableDateFields.startField] = toISOTimestamp(newStartDate);
      updateInput.itemData[editableDateFields.finishField] = toISOTimestamp(newEndDate);

      updateVisualizationItem(updateInput);
    },
    [
      canEdit,
      editableDateFields,
      items,
      timelineHeaderOptions,
      timelineSubHeaderOptions,
      updateVisualizationItem,
      layout.combineMultiSelectHeaders,
      customFieldsById,
    ],
  );

  const handleItemResize = useCallback(
    (itemId: number, newStartDate: string, newEndDate: string) => {
      if (!canEdit || !editableDateFields) {
        return;
      }
      const updateInput = createEmptyUpdateInput(itemId);
      updateInput.itemData[editableDateFields.startField] = toISOTimestamp(newStartDate);
      updateInput.itemData[editableDateFields.finishField] = toISOTimestamp(newEndDate);
      updateVisualizationItem(updateInput);
    },
    [canEdit, editableDateFields, updateVisualizationItem],
  );

  const {
    deferredFrom,
    deferredTo,
    firstItemWeekStartDate,
    lastItemWeekEndDate,
    minimapStart,
    minimapEnd,
    deferredMinimapStart,
    deferredMinimapEnd,
  } = useTimelineMinimapDates({ from: effectiveFrom, to: effectiveTo, items: itemsData.items });

  const data = useTimelineTableData({
    from: deferredFrom || '',
    to: deferredTo || '',
    minimapStart: deferredMinimapStart,
    minimapEnd: deferredMinimapEnd,
    mainHeaders,
    subHeaders,
    items: filteredItemsWithChildren,
    timelineHeaderOptions,
    timelineSubHeaderOptions,
    layout,
    showEmptyHeaders,
    expandedChildren,
    mainHeaderConfig,
    subHeaderConfigs,
    itemRanks,
  });

  const { selectedIds, clearSelection, replaceSelection, handleItemSelect } =
    useTimelineSelection();

  const [rubberBandPreviewIds, setRubberBandPreviewIds] = useState<ReadonlySet<number> | null>(
    null,
  );

  const handleRubberBandSelect = useCallback(
    (itemIds: number[], additive: boolean) => {
      if (itemIds.length === 0 && !additive) {
        clearSelection();
        return;
      }
      const next = additive ? new Set([...selectedIds, ...itemIds]) : new Set(itemIds);
      replaceSelection(next);
    },
    [selectedIds, clearSelection, replaceSelection],
  );

  const isEmpty = !isFetching && items.length === 0;
  const hasNoItemsMatchingLayout = isEmpty && hasItemsIgnoringLayout;
  const noItemsMatchFilters = isEmpty && !hasItemsIgnoringLayout && hasFilters;
  const hasNoItems = isEmpty && !hasItemsIgnoringLayout && !hasFilters;
  const hasNoHeaders = !timelineHeader;
  const hasItemsButNoDates =
    !isFetching &&
    items.length > 0 &&
    firstItemWeekStartDate === null &&
    lastItemWeekEndDate === null;

  if (!deferredFrom || !deferredTo) return null;
  if (hasNoItems) return <VisualizationNoItemsState visualizationType="timeline" />;
  if (hasNoItemsMatchingLayout)
    return <VisualizationNoItemsMatchLayoutState onOpenFormatPanel={onOpenFormatPanel} />;
  if (hasNoHeaders) return <VisualizationNoHeadersState visualizationType="timeline" />;
  if (noItemsMatchFilters) return <VisualizationNoItemsMatchFiltersState />;
  if (hasItemsButNoDates) return <TimelineNoItemsWithDates />;

  return (
    <>
      <div className="flex flex-col max-w-full max-h-full overflow-hidden relative">
        {(!isPublishedMode || showMinimap) && (
          <TimelineMinimap
            visualizationId={visualizationId}
            viewId={viewId}
            from={deferredFrom}
            to={deferredTo}
            data={data}
            onChange={onDateChange}
            minimapStart={minimapStart}
            minimapEnd={minimapEnd}
            canEdit={canEdit || showMinimap}
            persistChanges={!showMinimap}
            scrollContainerRef={scrollContainerRef}
            expandedRows={expandedRows}
            expandedChildren={expandedChildren}
            itemColorById={itemColorById}
            isCompact={layout.theme === 'compact'}
          />
        )}

        <TimelineTable
          from={deferredFrom}
          to={deferredTo}
          data={data}
          canEdit={canEditDates}
          isPublishedMode={isPublishedMode}
          isExportMode={isExportMode}
          onItemDrop={handleItemDrop}
          onItemResize={handleItemResize}
          header1FieldId={header1FieldId}
          header2FieldId={header2FieldId}
          scrollContainerRef={scrollContainerRef}
          layout={layout}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          itemColorById={itemColorById}
          itemLabelsById={itemLabelsById}
          rowHeaderWidth={rowHeaderWidth}
          onRowHeaderWidthChange={setRowHeaderWidth}
          expandedChildren={expandedChildren}
          setExpandedChildren={setExpandedChildren}
          setReorderHeadersModalState={setReorderHeadersModalState}
          onItemClick={onItemClick}
          viewId={viewId}
          visualizationId={visualizationId}
          itemRanks={itemRanks}
          selectedIds={selectedIds}
          previewIds={rubberBandPreviewIds}
          onSelectionClick={handleItemSelect}
          onVisibleItemIdsChange={setVisibleItemIds}
        />

        {!isPublishedMode && (
          <>
            <TimelineRubberBand
              scrollContainerRef={scrollContainerRef}
              onSelect={handleRubberBandSelect}
              onClearSelection={clearSelection}
              onPreviewChange={setRubberBandPreviewIds}
            />
            <TimelineSelectionActionBar count={selectedIds.size} onClear={clearSelection} />
          </>
        )}

        {isFetching && (
          <div className="absolute inset-0 bg-bg-surface-base/50 flex items-center justify-center z-10">
            <Spinner size={24} />
          </div>
        )}

        {!isPublishedMode && canEdit && (
          <TimelineAddHeaderMenu
            header1FieldId={header1FieldId}
            header2FieldId={header2FieldId}
            allMainHeaders={mainHeaders}
            allSubHeaders={subHeaders}
            subHeaderConfigs={subHeaderConfigs}
          />
        )}

        {isPublishedMode && !hideLegendOverlay && publishedLegendItems?.length ? (
          <div className="absolute bottom-4 right-4 z-10">
            <Legend items={publishedLegendItems} triggerOnClick contentClassName="z-[80]" />
          </div>
        ) : null}
      </div>

      {reorderHeadersModalState && (
        <ReorderTimelineHeadersDialog
          isOpen={true}
          onClose={() => setReorderHeadersModalState(null)}
          headers={reorderHeadersModalState.isReorderingMainHeader ? mainHeaders : subHeaders}
          headerFieldId={
            reorderHeadersModalState.isReorderingMainHeader ? header1FieldId : header2FieldId
          }
          parentHeaderId={reorderHeadersModalState.parentHeaderId}
          parentFieldId={!reorderHeadersModalState.isReorderingMainHeader ? header1FieldId : null}
          visualizationId={visualizationId}
        />
      )}
    </>
  );
};
